
import Vue from "vue";
import { mapActions, mapGetters, mapMutations } from "vuex";
import { INTERVIEW_LISTING } from "@/store/modules/recruiter/constants";
import { ROOT_ERROR } from "@/store/modules/root/constants";

interface PreRecordedInterview {
  id: number;
  candidate_id: number;
  interviewed_by: number;
  job_id: number;
  created_at: string;
  interview_status: string;
  video_recording: string | null;
  extras: any;
  schedule_date: string;
  job_title: string;
  zappy_job_id: number | null;
  lang: number;
}

export default Vue.extend({
  name: "CandidatePreRecordedInterviews",
  data() {
    return {
      loading: false,
      loadingMore: false,
      preRecordedInterviews: [] as PreRecordedInterview[],
      hasMoreInterviews: false,
      currentPage: 0, // Start from page 0 for first load
      pageSize: 12 // Use limit 12 for pre-recorded interviews
    };
  },
  computed: {
    ...mapGetters("auth", {
      user: "GET_USER_DETAILS"
    }),
    
    // Filter only available interviews that user can take
    availableInterviews(): PreRecordedInterview[] {
      return this.preRecordedInterviews;
    }
  },
  mounted() {
    this.loadPreRecordedInterviews();
  },
  methods: {
    ...mapActions("auth", ["LOGOUT_USER"]),
    ...mapActions("recruiter", [INTERVIEW_LISTING]),
    ...mapMutations({
      set_root_error: ROOT_ERROR
    }),
    
    async loadPreRecordedInterviews() {
      this.loading = true;
      try {
        console.log("Loading pre-recorded interviews for candidate:", this.user?.id);
        
        // Call the store action to get pre-recorded interviews
        const response = await this[INTERVIEW_LISTING]({
          page: this.currentPage,
          limit: this.pageSize,
          candidate_id: this.user?.id,
          interview_id: undefined,
          job_id: undefined,
          interviewed_by: undefined
          // Do not pass interview_status here — list all pre-recorded interviews
        });

        console.log("API Response:", response);

        if (response && response.results) {
          // API returns all pre-recorded interviews without status filtering
          this.preRecordedInterviews = response.results;
          this.hasMoreInterviews = response.results.length === this.pageSize;
          
          console.log("Available interviews:", this.preRecordedInterviews);
        } else {
          this.preRecordedInterviews = [];
          this.hasMoreInterviews = false;
        }
      } catch (error) {
        console.error("Error loading pre-recorded interviews:", error);
        this.set_root_error(this.$t("common.error-loading-data", "Error loading data"));
      } finally {
        this.loading = false;
      }
    },
    
    async loadMoreInterviews() {
      this.loadingMore = true;
      try {
        this.currentPage += 1;
        const response = await this[INTERVIEW_LISTING]({
          page: this.currentPage,
          limit: this.pageSize,
          candidate_id: this.user?.id,
          interview_id: undefined,
          job_id: undefined,
          interviewed_by: undefined
          // Do not pass interview_status here — list all pre-recorded interviews
        });

        if (response && response.results) {
          this.preRecordedInterviews = [...this.preRecordedInterviews, ...response.results];
          this.hasMoreInterviews = response.results.length === this.pageSize;
        } else {
          this.hasMoreInterviews = false;
        }
      } catch (error) {
        console.error("Error loading more interviews:", error);
        this.set_root_error(this.$t("common.error-loading-data", "Error loading data"));
      } finally {
        this.loadingMore = false;
      }
    },
    
    async refreshInterviews() {
      await this.loadPreRecordedInterviews();
    },
    
    getDifficultyColor(difficultyLevel: string): string {
      // Use consistent dark gray color for all difficulty levels
      return "#4A5568";
    },
    
    takeInterview(interview: PreRecordedInterview) {
      // Navigate to the interview session using the existing interview start route
      this.$router.push(`/candidate/interviews/start/${interview.id}`);
    }
  }
});
